import { useGetOne, useTranslate } from "ra-core";
import type { ReactNode } from "react";

import {
  computeSaleSplit,
  type SaleSplitOutcome,
} from "../corretaje/corretajeSale";
import { formatMxn } from "../quoter/PlanSchedule";
import type { Colega } from "../types";
import { CorretajeSplitPanel } from "./CorretajeSplitPanel";
import { EMPTY, type Context } from "./CorretajeSaleFields";
import { todayIso } from "./operationRules";

const ReadRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div>
    <dt className="text-sm text-muted-foreground">{label}</dt>
    <dd className="text-base">{children}</dd>
  </div>
);

/** Read-only view: locked after the VoBo approval, or for roles that only read the sale. */
export const SaleSummary = ({ context }: { context: Context }) => {
  const translate = useTranslate();
  const { admin, operation, option, schemes } = context;
  const { data: colega } = useGetOne<Colega>(
    "colegas",
    { id: admin.colega_id ?? "" },
    { enabled: admin.colega_id != null },
  );
  const approved = admin.vobo_status === "aprobado";
  const outcome: SaleSplitOutcome | null = approved
    ? {
        ok: true,
        split: {
          case_code: admin.snapshot_case_code as NonNullable<
            typeof admin.snapshot_case_code
          >,
          scheme_id: admin.snapshot_scheme_id as NonNullable<
            typeof admin.snapshot_scheme_id
          >,
          colega_amount: admin.snapshot_colega_amount ?? 0,
          vendedora_amount: admin.snapshot_vendedora_amount ?? 0,
          opcionadora_amount: admin.snapshot_opcionadora_amount ?? 0,
          almikia_amount: admin.snapshot_almikia_amount ?? 0,
        },
      }
    : schemes.length === 0
      ? null
      : admin.commission_total == null
        ? { ok: false, message: translate("crm.corretaje.sale.split_empty") }
        : computeSaleSplit(
            admin,
            option,
            operation.sales_id,
            schemes,
            todayIso(),
          );

  return (
    <div className="space-y-4">
      <dl className="grid gap-3 sm:grid-cols-2" data-testid="sale-summary">
        <ReadRow label={translate("crm.corretaje.sale.shared")}>
          {translate(
            admin.shared_with_colega
              ? "crm.corretaje.sale.yes"
              : "crm.corretaje.sale.no",
          )}
        </ReadRow>
        {admin.shared_with_colega ? (
          <>
            <ReadRow label={translate("resources.deals.fields.colega_id")}>
              {colega?.full_name ?? translate("crm.colegas.restricted")}
            </ReadRow>
            <ReadRow label={translate("crm.corretaje.sale.found_by")}>
              {admin.colega_found_by
                ? translate(
                    `crm.corretaje.sale.found_by_options.${admin.colega_found_by}`,
                  )
                : EMPTY}
            </ReadRow>
          </>
        ) : null}
        <ReadRow label={translate("crm.corretaje.sale.property_owner")}>
          {translate(
            `crm.corretaje.sale.property_owner_options.${admin.property_owner}`,
          )}
        </ReadRow>
        <ReadRow label={translate("crm.corretaje.sale.opcionadora")}>
          {admin.property_owner === "colega"
            ? translate("crm.corretaje.sale.opcionadora_vendedora", {
                name: context.vendedoraText,
              })
            : context.opcionadoraText}
        </ReadRow>
        <ReadRow label={translate("crm.corretaje.sale.vendedora")}>
          {context.vendedoraText}
        </ReadRow>
        <ReadRow label={translate("crm.corretaje.sale.commission_total")}>
          <span className="tabular-nums">
            {admin.commission_total != null
              ? formatMxn(admin.commission_total)
              : EMPTY}
          </span>
        </ReadRow>
      </dl>
      <CorretajeSplitPanel outcome={outcome} isSnapshot={approved} />
    </div>
  );
};
